import {
  Box,
  VStack,
  HStack,
  Text,
  Heading,
  useColorModeValue,
} from '@chakra-ui/react';
import { Users } from 'lucide-react';
import { Icon } from '@chakra-ui/react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useUsers } from '../../Users/useUsers';
import { useDepartments } from '../../Departments/useDepartments';

interface DepartmentCommunication {
  content: string;
  departments?: string[];
  assigned_users?: string[];
}

interface DepartmentCommunicationsProps {
  sectionName: string;
  notes: DepartmentCommunication[];
}

/**
 * Department Communications Component
 * Displays department communications with special formatting
 */
export function DepartmentCommunications({
  sectionName,
  notes,
}: DepartmentCommunicationsProps) {
  const { users } = useUsers();
  const { departments } = useDepartments();
  const bgColor = useColorModeValue('blue.50', 'blue.900');
  const borderColor = useColorModeValue('blue.300', 'blue.600');
  const headerBg = useColorModeValue('blue.200', 'blue.700');
  const textColor = useColorModeValue('gray.700', 'gray.300');
  const deptColor = useColorModeValue('blue.700', 'blue.300');
  const userColor = useColorModeValue('gray.600', 'gray.400');

  // Helper function to get department name from ID
  const getDepartmentName = (deptId: string): string => {
    const dept = departments.find(d => d.id === deptId);
    return dept?.name || deptId;
  };

  // Helper function to get user name from ID
  const getUserName = (userId: string): string => {
    const user = users.find(u => u.id === userId || u.uid === userId);
    return user?.name || user?.Name || userId;
  };

  if (!notes || notes.length === 0) return null;

  return (
    <Box mb={4}>
      <Heading size="sm" color={deptColor} mb={2}>
        From {sectionName}:
      </Heading>
      <VStack align="stretch" spacing={2}>
        {notes.map((note, idx) => (
          <Box
            key={`${sectionName}-dept-${idx}`}
            bg={bgColor}
            borderWidth="2px"
            borderColor={borderColor}
            borderRadius="md"
            p={4}
            shadow="sm"
          >
            <HStack align="start" spacing={3}>
              <Icon as={Users} w={5} h={5} color="blue.600" mt={0.5} flexShrink={0} />
              <VStack align="stretch" spacing={2} flex={1}>
                {note.departments && note.departments.length > 0 && (
                  <HStack spacing={2} flexWrap="wrap">
                    <Text fontSize="xs" fontWeight="semibold" color={userColor}>
                      Departments:
                    </Text>
                    <Text fontSize="sm" fontWeight="medium" color={deptColor}>
                      {note.departments.map(getDepartmentName).join(', ')}
                    </Text>
                  </HStack>
                )}
                {note.assigned_users && note.assigned_users.length > 0 && (
                  <HStack spacing={2} flexWrap="wrap">
                    <Text fontSize="xs" fontWeight="semibold" color={userColor}>
                      Assigned To:
                    </Text>
                    <Text fontSize="xs" color={userColor}>
                      {note.assigned_users.map(getUserName).join(', ')}
                    </Text>
                  </HStack>
                )}
                <ReactMarkdown
                  remarkPlugins={[remarkGfm]}
                  components={{
                    p: ({ node, ...props }) => <Text color={textColor} fontSize="sm" mb={2} {...props} />,
                    strong: ({ node, ...props }) => <Text as="strong" fontWeight="bold" color={textColor} {...props} />,
                    em: ({ node, ...props }) => <Text as="em" fontStyle="italic" color={textColor} {...props} />,
                    ul: ({ node, children, ...props }) => (
                      <Box as="ul" pl={4} mb={2} color={textColor} style={{ listStyleType: 'disc' }} {...(props as any)}>
                        {children}
                      </Box>
                    ),
                    ol: ({ node, children, ...props }) => (
                      <Box as="ol" pl={4} mb={2} color={textColor} style={{ listStyleType: 'decimal' }} {...(props as any)}>
                        {children}
                      </Box>
                    ),
                    li: ({ node, ...props }) => <Box as="li" mb={1} color={textColor} fontSize="sm" {...(props as any)} />,
                    h1: ({ node, ...props }) => <Heading size="md" color={textColor} mb={2} {...props} />,
                    h2: ({ node, ...props }) => <Heading size="sm" color={textColor} mb={2} {...props} />,
                    h3: ({ node, ...props }) => <Heading size="xs" color={textColor} mb={2} {...props} />,
                    code: ({ node, ...props }) => <Text as="code" bg="gray.100" px={1} py={0.5} borderRadius="sm" fontSize="xs" fontFamily="mono" color={textColor} {...props} />,
                    pre: ({ node, ...props }) => <Box as="pre" bg="gray.100" p={2} borderRadius="sm" overflowX="auto" mb={2} {...(props as any)} />,
                  }}
                >
                  {note.content}
                </ReactMarkdown>
              </VStack>
            </HStack>
          </Box>
        ))}
      </VStack>
    </Box>
  );
}

